/**
 * 10.9GS — DISSOLUÇÃO na morte (shader). Em vez de encolher e sumir, o corpo do monstro se
 * desfaz em "cubos" de energia: pedaços somem por um ruído 3D no espaço do mundo, e a borda do
 * que ainda existe brilha na cor do Void. Custo: 1 hash no fragment shader (e `discard`), só
 * nos materiais de corpo das criaturas.
 *
 * Encadeia com o hook do RimLight (onBeforeCompile anterior é chamado antes) e entra na chave
 * de cache do programa (senão um material com dissolução emprestaria o programa de um sem).
 */
import * as THREE from "three";

export interface DissolveUniform { value: number }

export function applyDissolve(mat: THREE.MeshStandardMaterial, edgeColor = 0xc9a6ff): DissolveUniform {
  const existing = mat.userData.dissolve as DissolveUniform | undefined;
  if (existing) return existing;
  const u: DissolveUniform = { value: 0 };
  const uColor = { value: new THREE.Color(edgeColor) };
  mat.userData.dissolve = u;
  const prevHook = mat.onBeforeCompile;
  const prevKey = mat.customProgramCacheKey;
  mat.customProgramCacheKey = () => `${prevKey ? prevKey.call(mat) : ""}|vhDissolve`;
  mat.onBeforeCompile = (shader, renderer) => {
    if (prevHook) prevHook.call(mat, shader, renderer);
    shader.uniforms.uDissolve = u;
    shader.uniforms.uDissolveColor = uColor;
    shader.vertexShader = shader.vertexShader
      .replace("#include <common>", "#include <common>\nvarying vec3 vDsPos;")
      .replace("#include <project_vertex>", "#include <project_vertex>\nvDsPos = (modelMatrix * vec4( transformed, 1.0 )).xyz;");
    shader.fragmentShader = shader.fragmentShader
      .replace("#include <common>", "#include <common>\nvarying vec3 vDsPos;\nuniform float uDissolve;\nuniform vec3 uDissolveColor;\nfloat vhDsNoise;")
      .replace(
        "#include <clipping_planes_fragment>",
        `#include <clipping_planes_fragment>
        vhDsNoise = fract(sin(dot(floor(vDsPos * 7.0 + 0.5), vec3(12.9898, 78.233, 37.719))) * 43758.5453);
        if (uDissolve > 0.0 && vhDsNoise < uDissolve) discard;`,
      )
      .replace(
        "#include <lights_physical_fragment>",
        `if (uDissolve > 0.0) totalEmissiveRadiance += uDissolveColor * (1.0 - smoothstep(uDissolve, uDissolve + 0.14, vhDsNoise)) * 3.0;
        #include <lights_physical_fragment>`,
      );
  };
  mat.needsUpdate = true;
  return u;
}
